import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { login } from 'slices/authSlice'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import routes from 'constants/routes'

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Invalid email address').required('Email is required'),
	password: Yup.string().required('Password is required'),
})

const LoginFormCN = () => {
	const dispatch = useDispatch()

	const userLogin = useSelector((state: any) => state.userLogin)
	const { loading: userLoginLoading, error: userLoginError } = userLogin

	const getErrorMessage = (error: any): string => {
		if (!error) return ''
		if (error.message === 'Request failed with status code 401') {
			return 'Invalid email or password.'
		}
		if (error.message === 'Request failed with status code 403') {
			return 'Your account has been locked. Please contact support.'
		}
		if (error.message === 'Network Error') {
			return 'Unable to connect to the server. Please check your internet connection.'
		}
		return error.message || 'An unexpected error occurred. Please try again.'
	}

	// Generate a unique key on each mount to force Formik to start fresh
	const [formKey] = useState(() => Date.now())

	return (
		<Formik
			key={formKey}
			initialValues={{ email: '', password: '' }}
			validationSchema={validationSchema}
			onSubmit={(values, { setSubmitting }) => {
				dispatch(login(values))
				setSubmitting(false)
			}}
		>
			{({ isSubmitting, handleChange, handleBlur, values, errors, touched }) => (
				<Form className="space-y-5" autoComplete="off">
					<h2 className="text-2xl font-semibold text-center text-[#074f9b]">Member Login</h2>

					{userLoginError && (
						<Alert variant="danger">{getErrorMessage(userLoginError)}</Alert>
					)}

					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								name="email"
								type="email"
								placeholder="Enter your email"
								value={values.email}
								onChange={handleChange}
								onBlur={handleBlur}
								className="h-11"
							/>
							{touched.email && errors.email && (
								<p className="text-xs text-red-500 mt-1">{errors.email}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								name="password"
								type="password"
								placeholder="Enter your password"
								autoComplete="new-password"
								value={values.password}
								onChange={handleChange}
								onBlur={handleBlur}
								className="h-11"
							/>
							{touched.password && errors.password && (
								<p className="text-xs text-red-500 mt-1">{errors.password}</p>
							)}
						</div>
					</div>

					<div className="flex flex-col items-center gap-3">
						<Button
							type="submit"
							isLarge
							disabled={isSubmitting || userLoginLoading}
							className="w-full"
						>
							{isSubmitting || userLoginLoading ? (
								<>
									<Loader2 className="w-4 h-4 animate-spin" />
									Signing in...
								</>
							) : (
								'Login'
							)}
						</Button>
						<Link
							to={routes.FORGOT_PASSWORD}
							className="text-sm text-[#0080fe] hover:text-[#0073e6] no-underline"
						>
							Forgot Password?
						</Link>
					</div>
				</Form>
			)}
		</Formik>
	)
}

export default LoginFormCN
